import { HttpClient, PaginatedResponse, robotoHeaders } from "../../http";
import { MetadataChangeset } from "../metadataChangeset";
import { Token } from "../tokens";

import { type Device } from "./Device";

interface Options {
  abortSignal: AbortSignal;
  resourceOwnerId: string;
  searchParams: URLSearchParams;
  pageToken?: string;
}

interface UpdateDeviceRequest {
  metadata_changeset?: MetadataChangeset;
}

export class DeviceService {
  #httpClient: HttpClient;

  constructor(httpClient: HttpClient) {
    this.#httpClient = httpClient;
  }

  public async getDevicesByOrg(
    orgId: string,
    options?: Partial<Options>,
  ): Promise<PaginatedResponse<Device>> {
    const searchParams = new URLSearchParams();

    if (options?.pageToken !== undefined) {
      searchParams.append("page_token", options.pageToken);
    }

    const requestUrl = this.#httpClient.constructUrl(
      `v1/devices/org/${orgId}`,
      searchParams,
    );
    const response = await this.#httpClient.get(requestUrl, {
      signal: options?.abortSignal,
    });
    return response.json<PaginatedResponse<Device>>();
  }

  /**
   * Fetches all devices for an organization, handling pagination internally.
   * Returns a flat array of all Device objects.
   */
  public async getAllDevicesByOrg(
    orgId: string,
    options?: Partial<Pick<Options, "abortSignal">>,
  ): Promise<Device[]> {
    const allDevices: Device[] = [];
    let pageToken: string | undefined = undefined;

    while (true) {
      const response = await this.getDevicesByOrg(orgId, {
        abortSignal: options?.abortSignal,
        pageToken,
      });

      allDevices.push(...response.items);

      if (response.next_token) {
        pageToken = response.next_token;
      } else {
        break;
      }
    }

    return allDevices;
  }

  public async getDevice(
    deviceId: string,
    options?: Partial<Options>,
  ): Promise<Device> {
    const requestUrl = this.#httpClient.constructUrl(
      `v1/devices/id/${deviceId}`,
    );
    const response = await this.#httpClient.get(requestUrl, {
      signal: options?.abortSignal,
      headers: robotoHeaders({ resourceOwnerId: options?.resourceOwnerId }),
    });
    return await response.json<Device>();
  }

  public async getTokensForDevice(
    deviceId: string,
    options?: Partial<Options>,
  ): Promise<Token[]> {
    const requestUrl = this.#httpClient.constructUrl(
      `v1/devices/id/${deviceId}/tokens`,
    );
    const response = await this.#httpClient.get(requestUrl, {
      signal: options?.abortSignal,
      headers: robotoHeaders({ resourceOwnerId: options?.resourceOwnerId }),
    });
    return await response.json<Token[]>();
  }

  public async updateDevice(
    deviceId: string,
    updates: UpdateDeviceRequest,
    options?: Partial<Options>,
  ): Promise<Device> {
    if (!options?.resourceOwnerId) {
      throw Error("updateDevice requires an Org ID, none was provided");
    }

    const requestUrl = this.#httpClient.constructUrl(
      `v1/devices/id/${deviceId}`,
    );

    const response = await this.#httpClient.put(requestUrl, {
      idempotent: true,
      headers: robotoHeaders({ resourceOwnerId: options.resourceOwnerId }),
      body: JSON.stringify(updates),
    });

    return await response.json<Device>();
  }
}
